import React, { createContext, ReactNode, useContext } from 'react';

import { RenderLayerManagerClass } from './render-layer-manager';

export interface RenderLayerContextValue {
	renderLayerManager: RenderLayerManagerClass;
}

const RenderLayerContext = createContext<RenderLayerContextValue | null>(null);

interface RenderLayerProviderProps {
	renderLayerManager: RenderLayerManagerClass;
	children: ReactNode;
}

/**
 * RenderLayerProvider component that provides RenderLayerManagerClass to all child components
 * This eliminates the need to pass renderLayerManager as props through multiple levels
 */
export const RenderLayerProvider: React.FC<RenderLayerProviderProps> = ({
	renderLayerManager,
	children
}) => {
	const contextValue: RenderLayerContextValue = {
		renderLayerManager
	};

	return (
		<RenderLayerContext.Provider value={contextValue}>
			{children}
		</RenderLayerContext.Provider>
	);
};

/**
 * Hook to access the render layer manager from context
 * Must be used within a RenderLayerProvider
 */
export const useRenderLayerContext = (): RenderLayerContextValue => {
	const context = useContext(RenderLayerContext);
	if (!context) {
		throw new Error('useRenderLayerContext must be used within a RenderLayerProvider');
	}
	return context;
};

/**
 * Hook to access the render layer manager directly
 * Convenience hook that extracts renderLayerManager from context
 */
export const useRenderLayerManager = (): RenderLayerManagerClass => {
	const { renderLayerManager } = useRenderLayerContext();
	return renderLayerManager;
};

export default RenderLayerProvider;
